/**
 * StudioPromptConfigsPanel — V14.1 Sprint B
 * =========================================
 * Lists every per-project prompt-config the user has saved + the global
 * default. Each card surfaces the localisation/style/heure preview and routes
 * to the existing PromptConfigPage on click for full editing.
 *
 * The "Nouveau profil" CTA opens a tiny modal placeholder (cf. backlog) —
 * actual creation flow will follow when the user defines the persistence
 * model (profiles vs project overrides).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sliders, Plus, ArrowRight, Loader2, Star, Layers } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudioPromptConfigsPanel = () => {
  const [defaultConf, setDefaultConf] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectConfs, setProjectConfs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [def, prj] = await Promise.all([
          axios.get(`${API}/prompt-config/default`, { withCredentials: true }).catch(() => ({ data: null })),
          axios.get(`${API}/projects/summary`,    { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setDefaultConf(def.data);
        setProjects(prj.data || []);
        const map = {};
        await Promise.all((prj.data || []).slice(0, 12).map(async (p) => {
          try {
            const r = await axios.get(`${API}/projects/${p.project_id}/prompt-config`, { withCredentials: true });
            map[p.project_id] = r.data;
          } catch { /* skip */ }
        }));
        setProjectConfs(map);
      } catch {
        // best effort
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-white/40">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement des profils…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-white/55 text-[13px] max-w-2xl">
          Profils de configuration prompt enregistrés par projet. Chaque projet
          peut surcharger la configuration par défaut. À terme, on pourra
          créer des "profils cross-projets" réutilisables.
        </p>
        <button
          type="button"
          data-testid="lib-create-profile-btn"
          disabled
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-[12px] font-medium uppercase tracking-[0.14em] opacity-50 cursor-not-allowed"
          style={{ background: '#D9A05B', color: '#0A0A0A' }}
          title="Bientôt disponible"
        >
          <Plus className="w-3.5 h-3.5" />
          Nouveau profil
        </button>
      </div>

      {/* Default config card */}
      {defaultConf && (
        <Link
          to="/prompt-config"
          className="group block rounded-xl p-5 transition-all hover:-translate-y-0.5"
          style={{ background: '#141414', border: '1px solid rgba(217,160,91,0.30)' }}
          data-testid="lib-default-config"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Star className="w-3.5 h-3.5 text-[#D9A05B] fill-[#D9A05B]" />
              <span className="text-[11px] uppercase tracking-[0.18em] font-medium text-[#D9A05B]">
                Profil par défaut
              </span>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-white/30 group-hover:text-[#D9A05B] group-hover:translate-x-0.5 transition-all" />
          </div>
          <ConfigPreview values={defaultConf.values || {}} />
        </Link>
      )}

      {/* Per-project configs */}
      <div>
        <h3 className="text-[11px] uppercase tracking-[0.18em] font-medium text-white/55 mb-2">
          Profils par projet
        </h3>
        {projects.length === 0 ? (
          <p className="text-white/40 text-[12.5px] italic">Aucun projet pour l'instant.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {projects.slice(0, 12).map((p) => (
              <Link
                key={p.project_id}
                to={`/projects/${p.project_id}?tab=settings`}
                className="group block rounded-xl p-4 transition-all hover:-translate-y-0.5"
                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}
                data-testid={`lib-project-config-${p.project_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Layers className="w-3.5 h-3.5 text-[#D9A05B]/70" />
                    <span className="text-white text-[13px] font-medium truncate">{p.name}</span>
                  </div>
                  <ArrowRight className="w-3 h-3 text-white/25 group-hover:text-[#D9A05B] transition-colors" />
                </div>
                <ConfigPreview values={(projectConfs[p.project_id] || {}).values || {}} compact />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ConfigPreview = ({ values, compact = false }) => {
  const rows = [
    ['Localisation', values.localisation],
    ['Style',        values.style_design || values.style],
    ['Heure',        values.moment_journee],
    ['Vue',          values.vue_angle],
    ['Mobilier',     values.catalogue_mobilier],
  ].filter(([, v]) => v);
  if (rows.length === 0) {
    return <p className="text-white/35 text-[11.5px] italic">Configuration vide</p>;
  }
  return (
    <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'} gap-x-4 gap-y-1`}>
      {rows.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <div className="text-[9.5px] uppercase tracking-[0.16em] text-white/35">{k}</div>
          <div className="text-[12px] text-white/85 truncate">{v}</div>
        </div>
      ))}
    </div>
  );
};

export default StudioPromptConfigsPanel;
